import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
} from "react-bootstrap";

export default class HotelMaintenanceTicket extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      tickets: [],
      rooms: [],
      showModal: false,
      editItem: null,
      newItem: {
        storeId: "",
        roomId: "",
        description: "",
        status: "Open",
        resolvedAt: "",
      },
      isLoading: false,
      isSaving: false,
    };
  }

  componentDidMount() {
    this.loadAllData();
  }

  async loadAllData() {
    this.setState({ isLoading: true });
    try {
      const apiBase = process.env.REACT_APP_API || "";
      // load tickets (page=1,pageSize=500 by default)
      const ticketRes = await fetch(
        `${apiBase}HotelMaintenanceTicket?storeId=${this.context.storeId}&page=1&pageSize=500`
      );
      const roomRes = await fetch(
        `${apiBase}HotelRoom?storeId=${this.context.storeId}`
      );

      if (!ticketRes.ok) {
        const txt = await ticketRes.text();
        throw new Error(txt || "Failed to fetch tickets");
      }
      if (!roomRes.ok) {
        const txt = await roomRes.text();
        throw new Error(txt || "Failed to fetch rooms");
      }

      const ticketJson = await ticketRes.json();
      const roomJson = await roomRes.json();

      // ticketJson is expected to be { Total, Page, PageSize, Items }
      const tickets = Array.isArray(ticketJson.items) ? ticketJson.items : [];
      this.setState({
        tickets: tickets,
        rooms: roomJson,
        isLoading: false,
      });
    } catch (err) {
      console.error("Error loading maintenance tickets:", err);
      alert("Error loading data: " + (err.message || err));
      this.setState({ isLoading: false });
    }
  }

  // normalize incoming ticket to newItem shape
  normalizeTicketToNewItem(ticket) {
    return {
      storeId: ticket.storeId || this.context.storeId || "",
      roomId: ticket.roomId || "",
      description: ticket.description || "",
      status: ticket.status || "Open",
      resolvedAt: ticket.resolvedAt ? ticket.resolvedAt.split("T")[0] : "",
    };
  }

  handleShowModal = (item = null) => {
    if (item) {
      this.setState({
        showModal: true,
        editItem: item,
        newItem: this.normalizeTicketToNewItem(item),
      });
    } else {
      this.setState({
        showModal: true,
        editItem: null,
        newItem: {
          storeId: this.context.storeId || "",
          roomId: "",
          description: "",
          status: "Open",
          resolvedAt: "",
        },
      });
    }
  };

  handleCloseModal = () => this.setState({ showModal: false, editItem: null });

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      newItem: { ...prev.newItem, [name]: value },
    }));
  };

  formatDateLocal(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d)) return iso.split("T")[0];
      return d.toLocaleDateString();
    } catch {
      return iso.split("T")[0];
    }
  }

  // Save: creates or updates using the server DTOs
  handleSave = async () => {
    const { newItem, editItem } = this.state;
    const apiBase = process.env.REACT_APP_API || "";
    if (!newItem.description || newItem.description.trim() === "") {
      alert("Description is required");
      return;
    }

    this.setState({ isSaving: true });
    try {
      if (editItem) {
        // send UpdateMaintenanceTicketDto -> only allowed fields
        const payload = {
          description: newItem.description,
          status: newItem.status,
          markResolved: newItem.status === "Resolved", // server will set ResolvedAt if true
        };

        const res = await fetch(
          `${apiBase}HotelMaintenanceTicket/${editItem.maintenanceTicketId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to update ticket");
        }
        alert("Ticket updated");
      } else {
        // create
        const payload = {
          storeId: newItem.storeId,
          roomId: newItem.roomId || null,
          description: newItem.description,
          status: newItem.status,
        };
        const res = await fetch(`${apiBase}HotelMaintenanceTicket`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to create ticket");
        }
        alert("Ticket created");
      }

      this.handleCloseModal();
      await this.loadAllData();
    } catch (err) {
      console.error("Error saving maintenance ticket:", err);
      alert("Error saving ticket: " + (err.message || err));
    } finally {
      this.setState({ isSaving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;
    const apiBase = process.env.REACT_APP_API || "";
    try {
      const res = await fetch(`${apiBase}HotelMaintenanceTicket/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to delete ticket");
      }
      alert("Ticket deleted");
      this.loadAllData();
    } catch (err) {
      console.error("Error deleting ticket:", err);
      alert("Error deleting ticket: " + (err.message || err));
    }
  };

  handleResolve = async (ticket) => {
    if (!window.confirm("Mark this issue as resolved?")) return;
    const apiBase = process.env.REACT_APP_API || "";
    try {
      const res = await fetch(
        `${apiBase}HotelMaintenanceTicket/${ticket.maintenanceTicketId}/resolve`,
        {
          method: "PATCH",
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to resolve");
      }
      alert("Ticket marked resolved");
      this.loadAllData();
    } catch (err) {
      console.error("Error resolving ticket:", err);
      alert("Error resolving ticket: " + (err.message || err));
    }
  };

  render() {
    const {
      tickets,
      rooms,
      showModal,
      newItem,
      editItem,
      isLoading,
      isSaving,
    } = this.state;

    return (
      <div className="container mt-4">
        <h4>Maintenance Tickets</h4>

        <div className="mb-3 d-flex align-items-center">
          <Button
            variant="primary"
            onClick={() => this.handleShowModal()}
            disabled={isLoading}
          >
            Add Ticket
          </Button>
          {isLoading && (
            <div className="ms-3 d-flex align-items-center">
              <Spinner animation="border" size="sm" role="status" />
              <span className="ms-2">Loading...</span>
            </div>
          )}
        </div>

        <Table bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th>Room</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created</th>
              <th>Resolved</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center">
                  No tickets found
                </td>
              </tr>
            )}

            {tickets.map((t) => (
              <tr key={t.maintenanceTicketId}>
                <td>{t.roomNumber || "—"}</td>
                <td>{t.description}</td>
                <td>
                  {t.status === "Resolved" ? (
                    <Badge bg="success">{t.status}</Badge>
                  ) : t.status === "In Progress" ? (
                    <Badge bg="warning" text="dark">
                      {t.status}
                    </Badge>
                  ) : (
                    <Badge bg="danger">{t.status}</Badge>
                  )}
                </td>
                <td>{this.formatDateLocal(t.createdAt)}</td>
                <td>
                  {t.resolvedAt ? this.formatDateLocal(t.resolvedAt) : "—"}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => this.handleShowModal(t)}
                  >
                    Edit
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => this.handleDelete(t.maintenanceTicketId)}
                  >
                    Delete
                  </Button>{" "}
                  {t.status !== "Resolved" && (
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => this.handleResolve(t)}
                    >
                      Resolve
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Modal */}
        <Modal show={showModal} onHide={this.handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {editItem ? "Edit Ticket" : "Add Maintenance Ticket"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Room</Form.Label>
                <Form.Select
                  name="roomId"
                  value={newItem.roomId}
                  onChange={this.handleChange}
                >
                  <option value="">-- Select --</option>
                  {rooms.map((r) => (
                    <option key={r.roomId} value={r.roomId}>
                      {r.roomNumber}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  value={newItem.description}
                  onChange={this.handleChange}
                  placeholder="Describe maintenance issue"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={newItem.status}
                      onChange={this.handleChange}
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Resolved At</Form.Label>
                    <Form.Control
                      type="date"
                      name="resolvedAt"
                      value={newItem.resolvedAt || ""}
                      onChange={this.handleChange}
                    />
                    <Form.Text className="text-muted">
                      Optional - server will set final ResolvedAt on resolve.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={this.handleCloseModal}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" role="status" />{" "}
                  {editItem ? "Updating..." : "Saving..."}
                </>
              ) : editItem ? (
                "Update"
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
